/**
 * SubNav Component
 *
 * Secondary navigation bar showing views within the active section.
 * Displays pills/tabs for each view with role-based visibility.
 *
 * @see docs/architecture/2026-01-08/NAVIGATION-RESTRUCTURE-MASTER-PLAN.md
 */

import React from 'react';
import PropTypes from 'prop-types';
import {
  Palette,
  Wrench,
  Truck,
  Folder,
  Activity,
  Printer,
  Command,
  FilePlus,
  Layout,
  BarChart,
  List,
  AlertTriangle,
  GitCommit,
  User,
  Clock,
  History,
  Grid,
  FileText,
  Download,
  Users,
  Ticket,
  CheckCircle,
  MessageSquare,
  Bell,
  Book,
  Paintbrush,
} from 'lucide-react';
import { getVisibleViews, SECTIONS } from '../../config/navigationConfig';
import { isPlaceholderView } from '../../config/viewRegistry';

/**
 * Map icon names from config to Lucide components
 */
const ICON_MAP = {
  palette: Palette,
  wrench: Wrench,
  truck: Truck,
  folder: Folder,
  activity: Activity,
  printer: Printer,
  command: Command,
  'file-plus': FilePlus,
  layout: Layout,
  'bar-chart': BarChart,
  list: List,
  'alert-triangle': AlertTriangle,
  'git-commit': GitCommit,
  user: User,
  clock: Clock,
  history: History,
  grid: Grid,
  'file-text': FileText,
  download: Download,
  users: Users,
  ticket: Ticket,
  'check-circle': CheckCircle,
  'message-square': MessageSquare,
  bell: Bell,
  book: Book,
  paintbrush: Paintbrush,
};

/**
 * Get icon component from name
 */
function getIcon(iconName) {
  return ICON_MAP[iconName] || null;
}

/**
 * SubNav renders the secondary navigation for a section
 * @param {object} props
 * @param {string} props.sectionId - Active section ID
 * @param {string} props.activeView - Currently active view ID
 * @param {object} props.user - User object
 * @param {function} props.onViewClick - Handler for view clicks
 */
function SubNav({ sectionId, activeView, user, onViewClick }) {
  if (!sectionId) return null;

  const section = SECTIONS[sectionId];
  if (!section || section.renderAs === 'dropdown') return null;

  const visibleViews = getVisibleViews(sectionId, user);

  if (visibleViews.length === 0) return null;

  return (
    <div className="bg-slate-200 border-b border-gray-200">
      <div className="px-4 sm:px-6 lg:px-8">
        <div className="flex overflow-x-auto py-2 gap-1 scrollbar-hide">
          {visibleViews.map((view) => {
            const Icon = getIcon(view.icon);
            const isActive = activeView === view.id;
            const isPlaceholder = isPlaceholderView(view.id);

            return (
              <button
                key={view.id}
                onClick={() => !isPlaceholder && onViewClick(sectionId, view.id)}
                disabled={isPlaceholder}
                className={`
                  relative flex-shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg
                  transition-all
                  ${
                    isActive
                      ? 'bg-white text-purple-700 shadow-sm font-medium'
                      : isPlaceholder
                        ? 'text-gray-400 italic cursor-default'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-slate-100 font-medium'
                  }
                `}
                aria-current={isActive ? 'page' : undefined}
              >
                <span>{view.label}</span>
                {/* Gradient accent bar for active state */}
                {isActive && (
                  <span className="absolute bottom-0 left-2 right-2 h-0.5 bg-gradient-to-r from-purple-600 via-pink-500 to-orange-500 rounded-full" />
                )}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

SubNav.propTypes = {
  sectionId: PropTypes.string,
  activeView: PropTypes.string,
  user: PropTypes.object,
  onViewClick: PropTypes.func.isRequired,
};

SubNav.defaultProps = {
  sectionId: null,
  activeView: null,
  user: null,
};

export default SubNav;
